// @flow
import React, { useLayoutEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { S, A, DARK } from '~/app/ui/tokens';

type Props = {
  message: string,
  type: string
};

const ICONS = {
  SUCCESS: { icon: 'check_circle', color: '#5BD18B' },
  ERROR: { icon: 'error', color: '#FF8A8A' },
  DEFAULT: { icon: 'info', color: '#A9C6F0' }
};

// The page editor marks its header bar (Save draft, Publish…) with this attribute, and the message shows
// just below it so it never covers those buttons. Other screens leave it off: messages go in the top right corner.
export const TOAST_BELOW = 'data-toast-below';
// A panel on the right (Publish, the section inspector, page settings) marks itself with this one, and the
// message shows to its left, over the page, so it never covers the panel.
export const TOAST_BESIDE = 'data-toast-beside';
const EDGE = 16;
const GAP = 12;
const MIN_WIDTH = 240;
const MAX_WIDTH = 380;

/** The message's top: below the lowest marked header that is on screen, or near the top when none is. */
export const toastTop = (doc = document) => {
  const bottoms = [...doc.querySelectorAll(`[${TOAST_BELOW}]`)]
    .map(h => h.getBoundingClientRect().bottom)
    // Scrolled out of sight, or so tall the message would end up mid-screen: the corner is free then.
    .filter(b => b > 0 && b < (doc.defaultView?.innerHeight || Infinity) / 2);
  return bottoms.length ? Math.round(Math.max(...bottoms)) + GAP : EDGE;
};

/** Where the message goes: below the header, and left of any open side panel when there is room for it. */
export const toastPlace = (doc = document) => {
  const top = toastTop(doc);
  const lefts = [...doc.querySelectorAll(`[${TOAST_BESIDE}]`)]
    .map(p => p.getBoundingClientRect())
    .filter(r => r.width > 0 && r.height > 0) // closed panels are hidden
    .map(r => r.left);
  const left = lefts.length ? Math.min(...lefts) : null;
  // No panel, or one so wide (a narrow window) that there's no room beside it: the right edge.
  if (left === null || left - 2 * EDGE < MIN_WIDTH) return { top, right: EDGE, maxWidth: MAX_WIDTH };
  const width = doc.documentElement.clientWidth || doc.defaultView?.innerWidth || 0;
  return { top, right: Math.round(width - left) + EDGE, maxWidth: Math.min(MAX_WIDTH, Math.floor(left - 2 * EDGE)) };
};

const samePlace = (a, b) => a.top === b.top && a.right === b.right && a.maxWidth === b.maxWidth;

// A graphite toast in both themes: in dark it is a raised surface, so the bright icon colors keep their contrast.
const useStyles = createUseStyles({
  toast: {
    background: S.fg,
    color: S.onFg,
    boxShadow: A.pop,
    [DARK]: { background: A.surface, color: S.fg, boxShadow: `0 0 0 1px ${S.line}, ${A.pop}` }
  }
});

/** A short message at the top right after an action (in the page editor, clear of its buttons and panels): what happened, or what went wrong. */
function FlashMessage({ message, type }: Props) {
  const c = useStyles();
  const look = ICONS[type] || ICONS.DEFAULT;
  // Placed before it paints, and moved (before the next paint) when the screen changes under it: a new
  // message, a panel opening or closing (Save draft, then Publish… right away), the window resizing.
  const [place, setPlace] = useState(() => (typeof document === 'undefined' ? { top: EDGE, right: EDGE, maxWidth: MAX_WIDTH } : toastPlace()));
  useLayoutEffect(() => {
    const update = () => { const now = toastPlace(); setPlace(was => (samePlace(was, now) ? was : now)); };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', update);
    return () => { observer.disconnect(); window.removeEventListener('resize', update); };
  }, [message]);
  return (
    <div
      className={c.toast}
      role={type === 'ERROR' ? 'alert' : 'status'}
      data-testid="flash-message"
      data-type={type}
      style={{
        position: 'fixed',
        top: place.top,
        right: place.right,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        boxSizing: 'border-box',
        minWidth: MIN_WIDTH,
        maxWidth: place.maxWidth,
        padding: '12px 16px 12px 12px',
        borderRadius: 10,
        fontFamily: S.ui,
        fontSize: 14,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap'
      }}
    >
      <i className="material-icons" aria-hidden style={{ fontSize: 20, color: look.color, flexShrink: 0 }}>{look.icon}</i>
      <span>{message}</span>
    </div>
  );
}

export default FlashMessage;
